import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CheckoutV2Service } from '../../services/checkout-v2.service';
import { OnlinePaymentService } from '../../services/online-payment.service';
import { PaymentEventsService } from '../../services/payment-events.service';
import { DataService } from '../../services/data.service';
import { AlertDialogComponent } from '../../shared/alert-dialog/alert-dialog.component';
import { MastercardPayload } from '../../models/payment.model';
import { PayAtTableNiPayload } from '../../models/pay-at-table.model';

@Component({
  selector: 'app-checkout-v2',
  templateUrl: './checkout-v2.component.html',
  styleUrls: ['./checkout-v2.component.scss'],
  standalone: false
})
export class CheckoutV2Component implements OnInit, OnDestroy {

  public loading = true;
  public readonly flowContainerId = 'checkout-v2-flow-container';

  private payload: MastercardPayload | PayAtTableNiPayload | null = null;
  private transactionId: string | null = null;
  private paymentSessionId: string | null = null;
  private chequeId: string | null = null;

  constructor(
    private checkoutV2Service: CheckoutV2Service,
    private onlinePaymentService: OnlinePaymentService,
    private paymentEventsService: PaymentEventsService,
    private dataService: DataService,
    private dialog: MatDialog,
    private router: Router,
    private location: Location,
  ) { }

  public ngOnInit(): void {
    const payload = this.onlinePaymentService.consumePendingPayload();

    if (!payload) {
      this.location.back();
      return;
    }

    this.payload = payload;
    this.chequeId = 'cheque_id' in payload ? payload.cheque_id ?? null : null;

    this.startSession();
  }

  private startSession(): void {
    if (!this.payload) {
      return;
    }

    this.loading = true;
    this.checkoutV2Service.getSessionDetails(this.payload).subscribe({
      next: (sessionDetails) => {
        this.transactionId = sessionDetails.transactionId;
        this.paymentSessionId = sessionDetails.paymentSessionId;
        this.checkoutV2Service.mount(this.flowContainerId, sessionDetails, {
          onPaymentCompleted: (paymentId) => this.handlePaymentResult(paymentId),
          onDeclined: () => this.handleDeclinedPayment(),
          onFatalError: () => this.handleFatalError(),
        }).then(() => this.loading = false);
      },
      // The session itself couldn't be created (backend/network down) — retrying
      // immediately would just loop, so stop and let the shopper back out instead.
      error: () => this.handleFatalError(),
    });
  }

  public ngOnDestroy(): void {
    this.checkoutV2Service.unmount();
  }

  // Bound to the top-bar's customBack input.
  public cancel = (): void => {
    this.paymentEventsService.notifyPaymentCancelled();
    this.location.back();
  };

  private handlePaymentResult(paymentId: string): void {
    if (!this.paymentSessionId || !this.transactionId) {
      // Mount succeeded but our own session state is missing — an internal bug,
      // not a card decline. Don't retry into the same broken state.
      this.handleFatalError();
      return;
    }

    this.checkoutV2Service.validateCheckoutV2Session({
      paymentId,
      paymentSessionId: this.paymentSessionId,
      transactionId: this.transactionId,
      chequeId: this.chequeId,
    }).subscribe((validated) => {
      if (!validated) {
        this.handleFatalError();
        return;
      }
      this.dataService.transactionLoaded.set(false);
      this.dialog.closeAll();
      this.router.navigate(['/bill-paid'], {
        queryParams: { table_id: this.dataService.table()?.id, transaction_id: this.transactionId },
      });
    });
  }

  private handleDeclinedPayment(): void {
    this.paymentEventsService.notifyPaymentCancelled();
    AlertDialogComponent.open(this.dialog, {
      type: 'error', title: 'PAYMENT.TITLE', message: 'PAYMENT.ERROR', enableCancelBtn: false,
    });
    this.checkoutV2Service.unmount();
    this.startSession();
  }

  private handleFatalError(): void {
    this.paymentEventsService.notifyPaymentCancelled();
    AlertDialogComponent.open(this.dialog, {
      type: 'error', title: 'PAYMENT.TITLE', message: 'PAYMENT.ERROR', enableCancelBtn: false,
    });
    this.checkoutV2Service.unmount();
    this.location.back();
  }

}
